import { BRAND_PALETTE } from '@worldcoo/shared/brand-palette';
import { useEffect, useId, useLayoutEffect, useRef, type RefObject } from 'react';
import { usePrefersReducedMotion } from '../../hooks/use-prefers-reduced-motion';
import {
	AMERICAS,
	EURASIA_AFRICA,
	FAR_HEARTS,
	FAR_SIDE,
	FRONT_HEARTS,
	HEART_MIDDLE_ABOVE_TIP,
	HEART_PATH,
	HEART_TIP,
	OUTLINE,
	SURFACE_CENTER_X,
	SURFACE_RADIUS,
} from './globe-art';
import {
	chain,
	fromSurface,
	measureRows,
	parsePath,
	pinTransform,
	projectPath,
	toLongitude,
	unproject,
	wrapLongitude,
} from './globe-projection';

/** One full turn, which is also the loop of a rendered video. */
export const TURN_MS = 36_000;
const TAU = Math.PI * 2;
/** Radians past the near edge a heart turns, hidden, before it pops in: 35 degrees, 3.5 s of the turn. */
const APPEAR_AFTER = (35 / 180) * Math.PI;
/** Radians before the far edge over which a heart scales down to nothing. */
const LEAVE_OVER = (30 / 180) * Math.PI;
const POP_MS = 500;
/** On the globe's first frame, the hearts on the face pop in one by one, west to east, after this lead. */
const FIRST_POP_MS = 600;
const FIRST_POP_STAGGER_MS = 150;
/** An ease-out-back overshoot: the pop peaks about 10% over full size before it settles. */
const POP_BACK = 1.7;

function popIn(elapsed: number) {
	const t = Math.min(1, Math.max(0, elapsed / POP_MS)) - 1;
	return 1 + (POP_BACK + 1) * t ** 3 + POP_BACK * t ** 2;
}

function onFace(longitude: number, angle: number) {
	return Math.cos(wrapLongitude(longitude + angle)) > 0;
}

/** On the face, and far enough past the near edge that the heart has had time to be seen arriving. */
function inWindow(longitude: number, angle: number) {
	const turned = wrapLongitude(longitude + angle);
	return turned > APPEAR_AFTER - Math.PI / 2 && turned < Math.PI / 2;
}

/** Already shrinking towards the far edge: a heart that starts here stays hidden until it comes round again. */
function leavingFace(longitude: number, angle: number) {
	return wrapLongitude(longitude + angle) > Math.PI / 2 - LEAVE_OVER;
}

/** Full size across the face, easing down to 0 as the heart nears the far edge. */
function leaving(turned: number) {
	const t = Math.min(1, Math.max(0, (Math.PI / 2 - turned) / LEAVE_OVER));
	return t * t * (3 - 2 * t);
}

function inked(ink: string) {
	return {
		fill: 'none',
		stroke: ink,
		strokeWidth: 1.44141,
		strokeLinecap: 'round',
		strokeLinejoin: 'round',
	} satisfies React.SVGProps<SVGPathElement>;
}

const rows = measureRows(parsePath(OUTLINE));
const americas = unproject(rows, parsePath(AMERICAS));
const eurasia = unproject(rows, parsePath(EURASIA_AFRICA));
const farSide = (d: string, offset = 0) => fromSurface(parsePath(d), SURFACE_CENTER_X, SURFACE_RADIUS, offset);

const philippines = farSide(FAR_SIDE.philippines);
const borneo = farSide(FAR_SIDE.borneo);
const australia = farSide(FAR_SIDE.australia);
const backToEurope = { start: eurasia.start, cubics: [] };

/**
 * The globe starts turned 60 degrees west: Eurasia faces the viewer and the Americas wait just behind the near
 * edge, so their hearts turn in and pop within the first seconds.
 */
const START = (-60 / 180) * Math.PI;

/** `still` is the face at `START`, before the first frame: what SSR and reduced motion show. */
const SURFACES = [
	{ key: 'americas', surface: chain(farSide(FAR_SIDE.pacificCoast, -TAU), americas) },
	{ key: 'eurasia', surface: chain(eurasia, farSide(FAR_SIDE.asia), backToEurope) },
	{ key: 'philippines', surface: philippines },
	{ key: 'borneo', surface: borneo },
	{ key: 'australia', surface: australia },
].map((entry) => ({ ...entry, still: projectPath(rows, entry.surface, START) }));

const PLACED = [
	...FRONT_HEARTS.map((heart) => ({
		...heart,
		longitude: toLongitude(rows, heart.x, heart.y - HEART_MIDDLE_ABOVE_TIP),
	})),
	...FAR_HEARTS.map((heart) => ({ ...heart, longitude: (heart.x - SURFACE_CENTER_X) / SURFACE_RADIUS })),
];
/** The drawn hearts keep their drawn width until they turn further out than any of them sits. */
const FLAT = Math.max(
	...FRONT_HEARTS.map((heart) => Math.abs(toLongitude(rows, heart.x, heart.y - HEART_MIDDLE_ABOVE_TIP))),
);

const HEARTS = PLACED.map((heart) => {
	const pin = leavingFace(heart.longitude, START)
		? null
		: pinTransform(rows, heart.longitude, heart.y - HEART_MIDDLE_ABOVE_TIP, START, FLAT);
	return { ...heart, still: { transform: pin ?? 'scale(0 1)', visibility: pin ? 'visible' : 'hidden' } };
});

/** Draws the coasts and hearts at an angle; the hearts pop from what earlier calls drew, so call it in time order. */
function globePainter(svg: SVGSVGElement) {
	const coasts = [...svg.querySelectorAll('[data-coast]')];
	const hearts = [...svg.querySelectorAll('[data-heart]')];

	/** When each heart pops in: a time still ahead keeps it hidden until then. */
	const poppedAt: (number | null)[] = HEARTS.map(() => null);
	const westToEast = HEARTS.map((heart, index) => ({ index, x: heart.x })).sort((a, b) => a.x - b.x);
	let firstFrame = true;

	const paint = (angle: number, now: number) => {
		if (firstFrame) {
			firstFrame = false;
			westToEast
				.filter(({ index }) => onFace(HEARTS[index]?.longitude ?? 0, angle))
				.forEach(({ index }, order) => {
					const heart = HEARTS[index];
					poppedAt[index] =
						heart && leavingFace(heart.longitude, angle) ? Infinity : now + FIRST_POP_MS + order * FIRST_POP_STAGGER_MS;
				});
		}
		SURFACES.forEach((entry, index) => coasts[index]?.setAttribute('d', projectPath(rows, entry.surface, angle)));
		HEARTS.forEach((heart, index) => {
			if (!onFace(heart.longitude, angle)) poppedAt[index] = null;
			else if (inWindow(heart.longitude, angle)) poppedAt[index] ??= now;
			const popped = poppedAt[index] ?? null;
			const pin = pinTransform(rows, heart.longitude, heart.y - HEART_MIDDLE_ABOVE_TIP, angle, FLAT);
			const size = popped === null ? 0 : popIn(now - popped) * leaving(wrapLongitude(heart.longitude + angle));
			const shown = pin !== null && size > 0.001;
			const scale = Math.round(size * 1000) / 1000;
			const transform = shown ? `${pin} translate(0 ${heart.y}) scale(${scale}) translate(0 ${-heart.y})` : null;
			hearts[index]?.setAttribute('transform', transform ?? heart.still.transform);
			hearts[index]?.setAttribute('visibility', shown ? 'visible' : 'hidden');
		});
	};

	const reset = () => {
		SURFACES.forEach((entry, index) => coasts[index]?.setAttribute('d', entry.still));
		HEARTS.forEach((heart, index) => {
			hearts[index]?.setAttribute('transform', heart.still.transform);
			hearts[index]?.setAttribute('visibility', heart.still.visibility);
		});
	};
	return { paint, reset };
}

/** The angle `ms` into the turning, which starts at `START`. */
function angleAt(ms: number) {
	return (START + (ms / TURN_MS) * TAU) % TAU;
}

/** Paints each new `at` with no clock of its own; going back in time starts the hearts over. */
function useSteppedClock(ref: RefObject<SVGSVGElement | null>, at: number | undefined) {
	const last = useRef<{ painter: ReturnType<typeof globePainter>; at: number } | null>(null);
	useLayoutEffect(() => {
		const svg = ref.current;
		if (at === undefined || !svg) return;
		const painter = last.current && last.current.at <= at ? last.current.painter : globePainter(svg);
		last.current = { painter, at };
		painter.paint(angleAt(at), at);
	}, [ref, at]);
}

/**
 * Land and hearts turn behind a fixed outline: each point slides along its latitude as on a sphere. `at` draws
 * it that many milliseconds into its turning instead of on its own clock, for a frame-by-frame render.
 */
export function GlobeAnimation(props: {
	label: string;
	className?: string;
	ink?: string;
	accent?: string;
	at?: number;
}) {
	const reducedMotion = usePrefersReducedMotion();
	const svgRef = useRef<SVGSVGElement>(null);
	const clipId = useId();
	const stepped = props.at !== undefined;
	const line = inked(props.ink ?? BRAND_PALETTE.webInk);

	useEffect(() => {
		const svg = svgRef.current;
		if (reducedMotion || stepped || !svg) return;
		const painter = globePainter(svg);

		let angle = START;
		let last: number | null = null;
		let frame = 0;
		const tick = (now: number) => {
			frame = requestAnimationFrame(tick);
			if (last !== null) angle = (angle + ((now - last) / TURN_MS) * TAU) % TAU;
			last = now;
			painter.paint(angle, now);
		};
		const observer = new IntersectionObserver(([entry]) => {
			cancelAnimationFrame(frame);
			last = null;
			if (entry?.isIntersecting) frame = requestAnimationFrame(tick);
		});
		observer.observe(svg);
		return () => {
			observer.disconnect();
			cancelAnimationFrame(frame);
			painter.reset();
		};
	}, [reducedMotion, stepped]);
	useSteppedClock(svgRef, props.at);

	return (
		<svg
			ref={svgRef}
			role="img"
			aria-label={props.label}
			viewBox="0 0 369 328"
			width={369}
			height={328}
			fill="none"
			className={props.className}
			// Every frame rewrites the coasts: this keeps that relayout and repaint inside the globe.
			style={{ contain: 'layout paint' }}
		>
			<clipPath id={clipId}>
				<path d={OUTLINE} />
			</clipPath>
			<g clipPath={`url(#${clipId})`}>
				{SURFACES.map((entry) => (
					<path key={entry.key} data-coast d={entry.still} {...line} />
				))}
				{HEARTS.map((heart) => (
					<g key={heart.country} data-heart {...heart.still}>
						<path
							d={HEART_PATH}
							fill={props.accent ?? BRAND_PALETTE.webOrange}
							transform={`translate(${-HEART_TIP.x} ${heart.y - HEART_TIP.y})`}
						/>
					</g>
				))}{' '}
			</g>
			<path d={OUTLINE} {...line} />
		</svg>
	);
}
